/** SUNTRONIX ONE — ONE NETWORK · Professional Email Template Library & Manager.
 *  Reusable, versioned, personalisable relationship follow-up templates.
 *  Add / Edit / Duplicate / Safe-Delete / Activate / Set-Default / Preview /
 *  Test-Send / Version-History / Create-with-AI. */
import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Mail, Plus, Sparkles, Copy, Pencil, Eye, Send, Star, Power, History, Trash2,
  MoreVertical, Bold, Italic, Underline, List, ListOrdered, Link2, ShieldCheck,
} from "lucide-react";

// §11/§22 — normalise pasted content to the SUNTRONIX standard (strip foreign
// fonts/sizes, keep bold/italic/underline/links/lists).
const normalisePastedHtml = (html) => (html || "")
  .replace(/<\/?(o:p|xml|meta|style|script)[^>]*>/gi, "")
  .replace(/<!--[\s\S]*?-->/g, "")
  .replace(/<\/?font[^>]*>/gi, "")
  .replace(/font-family\s*:[^;"']*;?/gi, "")
  .replace(/font-size\s*:[^;"']*;?/gi, "")
  .replace(/mso-[^;"']*;?/gi, "")
  .replace(/style\s*=\s*"\s*"/gi, "")
  .replace(/style\s*=\s*'\s*'/gi, "");

const TABS = ["ALL", "BUSINESS", "CONFERENCE", "CLIENT", "VENDOR", "ASSOCIATION",
  "VISITOR", "PERSONAL", "RECONNECT", "MY_TEMPLATES", "INACTIVE"];
const TAB_LABEL = {
  ALL: "All", BUSINESS: "Business", CONFERENCE: "Conference / Delegation", CLIENT: "Client",
  VENDOR: "OEM / Vendor / Partner", ASSOCIATION: "Association / Club", VISITOR: "Visitor",
  PERSONAL: "Personal / Social", RECONNECT: "Reconnect", MY_TEMPLATES: "My Templates", INACTIVE: "Inactive",
};
const OWN_STYLE = {  SYSTEM: "bg-slate-100 text-slate-600 border-slate-200",
  SUNTRONIX: "bg-sky-50 text-sky-700 border-sky-200",
  TEAM: "bg-violet-50 text-violet-700 border-violet-200",
  PERSONAL: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function EmailTemplates() {
  const [tab, setTab] = useState("ALL");
  const [data, setData] = useState({ items: [], counts: {} });
  const [meta, setMeta] = useState({ categories: [], levels: [], lengths: [], variables: [], is_admin: false });
  const [editing, setEditing] = useState(null);      // template object or {} for new
  const [previewId, setPreviewId] = useState(null);
  const [testId, setTestId] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [defaultFor, setDefaultFor] = useState(null); // template for set-default

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/one-network/templates?tab=${tab}`);
      setData(d);
    } catch { toast.error("Could not load templates"); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/one-network/templates/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Action failed"); } };

  const doDelete = async (t) => {
    if (!window.confirm(`Delete “${t.name}”?\nThis removes it from the Template Library.`)) return;
    try {
      await api.delete(`/one-network/templates/${t.id}`);
      toast.success("Template deleted"); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det?.can_deactivate) {
        if (window.confirm(`${det.message}\n\nDeactivate it now?`)) {
          act(() => api.post(`/one-network/templates/${t.id}/deactivate`), "Template deactivated");
        }
      } else toast.error("Could not delete");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-5" data-testid="email-templates-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-indigo-600 grid place-items-center"><Mail className="h-4 w-4 text-white" /></span>
            <h1 className="text-lg font-semibold text-slate-900">Email Templates</h1>
          </div>
          <p className="text-[12px] text-slate-500 mt-0.5">Personalised relationship follow-ups · Verdana 12pt · bold greeting · versioned</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-template-menu"><Plus className="h-4 w-4 mr-1.5" /> Create Template</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid="create-blank" onClick={() => setEditing({ category: "BUSINESS", ownership: "PERSONAL", attach_profile: "ASK", signature_handling: "ONE_INSERTS", subject_template: "", body_template: "" })}>
              <Pencil className="h-4 w-4 mr-2" /> Blank template
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="create-ai" onClick={() => setAiOpen(true)}>
              <Sparkles className="h-4 w-4 mr-2" /> Create with AI
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="template-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-2.5 py-1 rounded-full text-[12px] border transition-colors ${tab === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {TAB_LABEL[t]}{data.counts?.[t] != null && <span className="ml-1 opacity-70">{data.counts[t]}</span>}
          </button>
        ))}
      </div>

      {data.items.length === 0 ? (
        <div className="text-center text-[13px] text-slate-400 py-16 border border-dashed rounded-xl">No templates in this view.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="template-grid">
          {data.items.map((t) => (
            <div key={t.id} className={`rounded-xl border bg-white p-3.5 flex flex-col ${t.active === false ? "opacity-70 border-slate-200" : "border-slate-200"}`} data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{t.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{t.subject_template}</div>
                </div>
                <TemplateMenu t={t} isAdmin={meta.is_admin} onEdit={() => setEditing(t)} onPreview={() => setPreviewId(t.id)}
                  onTest={() => setTestId(t.id)} onHistory={() => setHistoryId(t.id)} onDefault={() => setDefaultFor(t)}
                  onDuplicate={() => act(() => api.post(`/one-network/templates/${t.id}/duplicate`), "Template duplicated")}
                  onToggle={() => act(() => api.post(`/one-network/templates/${t.id}/${t.active === false ? "activate" : "deactivate"}`), t.active === false ? "Activated" : "Deactivated")}
                  onDelete={() => doDelete(t)} />
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <Badge className={`text-[10px] border ${OWN_STYLE[t.ownership] || OWN_STYLE.SYSTEM}`}>{t.ownership}</Badge>
                <Badge className="text-[10px] border bg-white text-slate-500 border-slate-200">{TAB_LABEL[t.category] || t.category}</Badge>
                <Badge className="text-[10px] border bg-white text-slate-400 border-slate-200">v{t.version}</Badge>
                {t.is_default && <Badge className="text-[10px] border bg-amber-50 text-amber-700 border-amber-200"><Star className="h-2.5 w-2.5 mr-0.5" /> Default</Badge>}
                {t.active === false && <Badge className="text-[10px] border bg-rose-50 text-rose-600 border-rose-200">Inactive</Badge>}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-2">Used {t.usage_count || 0}×</div>
            </div>
          ))}
        </div>
      )}

      {editing && <EditorDialog tpl={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {previewId && <PreviewDialog tid={previewId} meta={meta} onClose={() => setPreviewId(null)} />}
      {testId && <PreviewDialog tid={testId} meta={meta} test onClose={() => setTestId(null)} />}
      {historyId && <HistoryDialog tid={historyId} onClose={() => setHistoryId(null)} />}
      {defaultFor && <DefaultDialog tpl={defaultFor} onClose={() => setDefaultFor(null)} onSaved={() => { setDefaultFor(null); load(); }} />}
      {aiOpen && <AiDialog meta={meta} onClose={() => setAiOpen(false)} onDraft={(d) => { setAiOpen(false); setEditing(d); }} />}
    </div>
  );
}

const TemplateMenu = ({ t, isAdmin, onEdit, onPreview, onTest, onHistory, onDefault, onDuplicate, onToggle, onDelete }) => {
  const canEdit = t.can_edit;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`template-menu-${t.id}`}><MoreVertical className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="text-[13px]">
        <DropdownMenuItem data-testid={`tpl-preview-${t.id}`} onClick={onPreview}><Eye className="h-4 w-4 mr-2" /> Preview</DropdownMenuItem>
        <DropdownMenuItem data-testid={`tpl-test-${t.id}`} onClick={onTest}><Send className="h-4 w-4 mr-2" /> Test send to me</DropdownMenuItem>
        <DropdownMenuItem data-testid={`tpl-duplicate-${t.id}`} onClick={onDuplicate}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
        <DropdownMenuItem data-testid={`tpl-history-${t.id}`} onClick={onHistory}><History className="h-4 w-4 mr-2" /> Version history</DropdownMenuItem>
        {canEdit && <DropdownMenuSeparator />}
        {canEdit && <DropdownMenuItem data-testid={`tpl-edit-${t.id}`} onClick={onEdit}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>}
        {canEdit && <DropdownMenuItem data-testid={`tpl-default-${t.id}`} onClick={onDefault}><Star className="h-4 w-4 mr-2" /> Set as default</DropdownMenuItem>}
        {canEdit && <DropdownMenuItem data-testid={`tpl-toggle-${t.id}`} onClick={onToggle}><Power className="h-4 w-4 mr-2" /> {t.active === false ? "Activate" : "Deactivate"}</DropdownMenuItem>}
        {canEdit && <DropdownMenuItem data-testid={`tpl-delete-${t.id}`} onClick={onDelete} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ── rich-text editor ────────────────────────────────────────────────
const EditorDialog = ({ tpl, meta, onClose, onSaved }) => {
  const isNew = !tpl.id;
  const [f, setF] = useState({
    name: tpl.name || "", category: tpl.category || "BUSINESS",
    ownership: tpl.ownership || "PERSONAL", subject_template: tpl.subject_template || "",
    attach_profile: tpl.attach_profile || "ASK", signature_handling: tpl.signature_handling || "ONE_INSERTS",
  });
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (bodyRef.current) bodyRef.current.innerHTML = tpl.body_template || "<p></p>"; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const exec = (cmd, val) => { document.execCommand(cmd, false, val); bodyRef.current?.focus(); };
  const insertVar = (v) => { document.execCommand("insertText", false, `{${v}}`); bodyRef.current?.focus(); };
  const insertLink = () => { const url = window.prompt("Link URL", "https://"); if (url) exec("createLink", url); };
  const onPaste = (e) => {
    e.preventDefault();
    const cd = e.clipboardData;
    const html = cd.getData("text/html");
    const text = cd.getData("text/plain");
    if (html) document.execCommand("insertHTML", false, normalisePastedHtml(html));
    else document.execCommand("insertText", false, text);
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const payload = { ...f, body_template: bodyRef.current?.innerHTML || "" };
    try {
      if (isNew) await api.post("/one-network/templates", payload);
      else await api.patch(`/one-network/templates/${tpl.id}`, payload);
      toast.success(isNew ? "Template created" : "Template saved (new version)");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const TBTN = ({ icon: Ic, cmd, val, onClick, testid }) => (
    <button type="button" data-testid={testid} onMouseDown={(e) => { e.preventDefault(); onClick ? onClick() : exec(cmd, val); }}
      className="h-8 w-8 grid place-items-center rounded hover:bg-slate-100 text-slate-600 border border-transparent hover:border-slate-200"><Ic className="h-4 w-4" /></button>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle className="text-base">{isNew ? "New template" : `Edit — ${tpl.name}`}</DialogTitle>
          <DialogDescription className="text-[12px]">The greeting “Dear {"{"}SALUTATION_DISPLAY_NAME{"}"},” is added automatically in Verdana 12pt bold. Editing a used template creates a new version.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="tpl-name" /></Field>
          <Field label="Category">
            <select value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="tpl-category" className="h-9 w-full border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              {meta.categories.map((c) => <option key={c} value={c}>{meta.category_labels?.[c] || c}</option>)}
            </select>
          </Field>
          <Field label="Subject template"><Input value={f.subject_template} onChange={(e) => set("subject_template", e.target.value)} data-testid="tpl-subject" placeholder="Pleasure meeting you at {EVENT_NAME} | SUNTRONIX" /></Field>
          {meta.is_admin && (
            <Field label="Ownership">
              <select value={f.ownership} onChange={(e) => set("ownership", e.target.value)} data-testid="tpl-ownership" className="h-9 w-full border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                {["PERSONAL", "TEAM", "SUNTRONIX"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          )}
          <Field label="Attach SUNTRONIX profile">
            <select value={f.attach_profile} onChange={(e) => set("attach_profile", e.target.value)} data-testid="tpl-attach" className="h-9 w-full border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              {(meta.attach_modes || ["YES", "NO", "ASK"]).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Signature handling">
            <select value={f.signature_handling} onChange={(e) => set("signature_handling", e.target.value)} data-testid="tpl-signature" className="h-9 w-full border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              {(meta.signature_modes || ["ONE_INSERTS", "OUTLOOK_INSERTS", "ASK"]).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
        </div>

        <div className="mt-1">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Body</div>
          <div className="flex items-center gap-1 border border-slate-200 rounded-t-md px-1.5 py-1 bg-slate-50 flex-wrap">
            <TBTN icon={Bold} cmd="bold" testid="tb-bold" />
            <TBTN icon={Italic} cmd="italic" testid="tb-italic" />
            <TBTN icon={Underline} cmd="underline" testid="tb-underline" />
            <TBTN icon={List} cmd="insertUnorderedList" testid="tb-ul" />
            <TBTN icon={ListOrdered} cmd="insertOrderedList" testid="tb-ol" />
            <TBTN icon={Link2} onClick={insertLink} testid="tb-link" />
          </div>
          <div ref={bodyRef} contentEditable data-testid="tpl-body" onPaste={onPaste}
            className="min-h-[180px] border border-t-0 border-slate-200 rounded-b-md px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-indigo-300"
            style={{ fontFamily: "Verdana, Arial, sans-serif", fontSize: "12pt" }} suppressContentEditableWarning />
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Insert variable</div>
            <div className="flex flex-wrap gap-1">
              {meta.variables.map((v) => (
                <button key={v} type="button" data-testid={`var-${v}`} onMouseDown={(e) => { e.preventDefault(); insertVar(v); }}
                  className="px-1.5 py-0.5 rounded text-[10.5px] border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600">{`{${v}}`}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="tpl-save" className="bg-indigo-600 hover:bg-indigo-700">{isNew ? "Create" : "Save version"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>{children}</div>
);

// ── preview / test send ──────────────────────────────────────────────
const PreviewDialog = ({ tid, meta, test, onClose }) => {
  const [level, setLevel] = useState("WARM");
  const [length, setLength] = useState("STANDARD");
  const [ai, setAi] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async () => {
    setBusy(true);
    try {
      const url = `/one-network/templates/${tid}/${test ? "test-send" : "preview"}`;
      const { data } = await api.post(url, { level, length, ai });
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  }, [tid, test, level, length, ai]);
  useEffect(() => { run(); }, [tid, test]); // eslint-disable-line

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={test ? "template-testsend" : "template-preview"}>
        <DialogHeader>
          <DialogTitle className="text-base">{test ? "Test send to me" : "Preview"}{res?.template_name ? ` — ${res.template_name}` : ""}</DialogTitle>
          <DialogDescription className="text-[12px]">{res?.sample ? "Rendered with a sample contact (Mr. Tanaka)." : res?.to_name ? `Rendered for ${res.to_name}.` : ""}{res?.template_version ? ` · v${res.template_version}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="preview-level" className="h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
            {(meta.levels || ["WARM"]).map((l) => <option key={l} value={l}>{l.replace(/_/g, " ")}</option>)}
          </select>
          <select value={length} onChange={(e) => setLength(e.target.value)} data-testid="preview-length" className="h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white">
            {(meta.lengths || ["STANDARD"]).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <label className="text-[12px] text-slate-600 flex items-center gap-1.5"><input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} data-testid="preview-ai" /> AI personalise</label>
          <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="preview-regenerate"><Sparkles className="h-3.5 w-3.5 mr-1" /> Regenerate</Button>
        </div>

        {test && res?.setup_required && (
          <div className="text-[12px] rounded px-3 py-2 border bg-amber-50 border-amber-200 text-amber-800">{res.message}</div>
        )}
        {res?.missing_salutation && (
          <div className="text-[12px] rounded px-3 py-2 border bg-rose-50 border-rose-200 text-rose-700">Salutation required — set an honorific for this contact before sending.</div>
        )}

        {res && (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="preview-render">
            <div className="px-3 py-2 border-b border-slate-100 text-[12px] space-y-0.5">
              <div><span className="text-slate-400">To:</span> {res.to || "—"} {res.to_name ? `(${res.to_name})` : ""}</div>
              <div><span className="text-slate-400">Subject:</span> <b>{res.subject}</b></div>
            </div>
            <div className="px-4 py-3" dangerouslySetInnerHTML={{ __html: res.html_body }} />
            <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex flex-wrap gap-3">
              <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Attach profile: {res.attach_profile}</span>
              <span>Signature: {(res.signature_handling || "").replace(/_/g, " ")}</span>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── version history ───────────────────────────────────────────────────
const HistoryDialog = ({ tid, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/one-network/templates/${tid}`).then((r) => setD(r.data)).catch(() => {}); }, [tid]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="template-history">
        <DialogHeader><DialogTitle className="text-base">Version history</DialogTitle>
          <DialogDescription className="text-[12px]">{d?.template?.name} — currently v{d?.template?.version}. Sent emails keep the exact version used.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px]">
            <b>v{d?.template?.version}</b> — current
          </div>
          {(d?.versions || []).map((v) => (
            <div key={v.id} className="rounded border border-slate-200 px-3 py-2 text-[12px]">
              <div className="flex justify-between"><b>v{v.version}</b><span className="text-slate-400">{(v.published_at || "").slice(0, 10)}</span></div>
              <div className="text-slate-500 mt-0.5">{v.subject_template}</div>
            </div>
          ))}
          {d && (d.versions || []).length === 0 && <div className="text-[12px] text-slate-400 text-center py-4">No prior versions — this template has not been re-published yet.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── set default ───────────────────────────────────────────────────────
const DEFAULT_KEYS = ["BUSINESS_CARD", "CONFERENCE", "EXHIBITION", "DELEGATION", "CLIENT_MEETING",
  "CUSTOMER", "PROSPECT", "VENDOR", "OEM", "DISTRIBUTOR", "PARTNER", "ASSOCIATION", "STORE_VISIT", "SOCIAL", "MET_AGAIN"];
const DefaultDialog = ({ tpl, onClose, onSaved }) => {
  const [sel, setSel] = useState(tpl.default_for || []);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setSel((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/one-network/templates/${tpl.id}/set-default`, { default_for: sel }); toast.success("Default updated"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="template-default">
        <DialogHeader><DialogTitle className="text-base">Set as default</DialogTitle>
          <DialogDescription className="text-[12px]">“{tpl.name}” becomes the recommended template for the selected relationship / event / meeting types.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {DEFAULT_KEYS.map((k) => (
            <button key={k} type="button" data-testid={`default-key-${k}`} onClick={() => toggle(k)}
              className={`px-2 py-0.5 rounded-full text-[11px] border ${sel.includes(k) ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200"}`}>{k.replace(/_/g, " ")}</button>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="default-save" className="bg-amber-500 hover:bg-amber-600">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── create with AI ────────────────────────────────────────────────────
const AiDialog = ({ meta, onClose, onDraft }) => {
  const [prompt, setPrompt] = useState("");
  const [category, setCategory] = useState("BUSINESS");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!prompt.trim()) { toast.error("Describe the template you want"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/one-network/templates/ai-generate", { prompt, category });
      toast.success("Draft ready — review before saving");
      onDraft({ ...data, ownership: "PERSONAL", attach_profile: "ASK", signature_handling: "ONE_INSERTS" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="template-ai">
        <DialogHeader><DialogTitle className="text-base">Create with AI</DialogTitle>
          <DialogDescription className="text-[12px]">ONE proposes a template. You must review it before saving — ONE never invents facts.</DialogDescription>
        </DialogHeader>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ai-category" className="h-9 w-full border border-slate-200 rounded-md px-2 text-[13px] bg-white">
            {meta.categories.map((c) => <option key={c} value={c}>{meta.category_labels?.[c] || c}</option>)}
          </select>
        </Field>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} data-testid="ai-prompt"
          placeholder="e.g. Create a short formal follow-up email for Japanese delegates I met in Tokyo." />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={run} disabled={busy} data-testid="ai-generate" className="bg-indigo-600 hover:bg-indigo-700"><Sparkles className="h-4 w-4 mr-1.5" /> Generate draft</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
